import config from "@Config";
import IAuthenticationService from "@HisPlatform/Services/Definition/Authentication/IAuthenticationService";
import AccessToken from "@Toolkit/CommonWeb/Authentication/Definition/AccessToken";
import moment from "moment";
import { TypedAsyncEvent } from "@Toolkit/CommonWeb/TypedAsyncEvent";
import * as oauth from "oauth4webapi";
import Base64Converter from "@Toolkit/CommonWeb/Base64";
import sha256 from "crypto-js/sha256";
import Base64 from 'crypto-js/enc-base64Url';
import ILogger from "@HisPlatform/Services/Definition/Logger/ILogger";
import DateTimeService from "@Toolkit/ReactClient/Services/Implementation/DateTimeService/DateTimeService";
import { isNullOrEmptyString, isNullOrUndefined } from "@Toolkit/CommonWeb/NullCheckHelpers";

export enum AuthenticationServiceStartupState {
    Idle,
    InProgress,
    Success
}

export default class AuthenticationService implements IAuthenticationService {
    private static readonly AuthCodeVerifierName: string = "auth-code-verifier";

    private _currentToken: AccessToken = null;

    public readonly onLoggingOutAsyncEvent = new TypedAsyncEvent<void>();
    public readonly onLoggedInAsyncEvent = new TypedAsyncEvent<void>();
    public readonly onAccessTokenChangedAsyncEvent = new TypedAsyncEvent<string>();

    constructor(
        public logger: ILogger,
    ) { }

    public replaceLogger(newLogger: ILogger): ILogger {
        const old = this.logger;
        this.logger = newLogger;
        return old;
    }

    public async logOutAsync(): Promise<void> {
        this.logger.log("Logging out", "info");

        await this.onLoggingOutAsyncEvent.emitAsync();

        const { oAuthServer, client } = await this.initializeOAuthAsync();
        if (oAuthServer.end_session_endpoint) {
            const url = new URL(oAuthServer.end_session_endpoint!);
            url.searchParams.set('client_id', client.client_id);
            url.searchParams.set('post_logout_redirect_uri', `${self.location.protocol}//${self.location.host}/logoutCallback`);
            window.location.href = url.toString();
        }
    }

    public async getAccessTokenAsync(): Promise<string> {
        if (this._currentToken == null) {
            throw new Error("AuthenticationService is not started up.");
        }

        if (this.tokenWillBeExpiredIn(this._currentToken, 10)) {
            await this.renewTokenAsync();
        }

        return this._currentToken.tokenValue;
    }

    public async startupAsync(): Promise<AuthenticationServiceStartupState> {
        if (document.location.pathname == "/loginCallback") {
            const { returnUrl, hasCorrigibleError } = await this.processAuthenticationCallbackAsync();
            if (hasCorrigibleError) {
                window.location.replace(returnUrl);
            } else {
                window.history.replaceState(null, null, returnUrl);
            }
        } else {
            await this.authenticateAsync(`${document.location.pathname}${document.location.hash}${document.location.search}`);
            return AuthenticationServiceStartupState.InProgress;
        }

        return AuthenticationServiceStartupState.Success;
    }

    public setUnauthenticated(): void {
        this.logOutAsync().catch(console.error);
    }

    private async renewTokenAsync(): Promise<boolean> {

        const { oAuthServer, client } = await this.initializeOAuthAsync();

        const response = await oauth.refreshTokenGrantRequest(oAuthServer, client, this._currentToken.refreshTokenValue);
        const result = await oauth.processRefreshTokenResponse(oAuthServer, client, response);

        if (result.error) {
            this.logger.log(`OAuth2: Cannot refresh access token. Result: ${JSON.stringify(result)}`, "error");
            return false;
        }

        this.logger.log("OAuth2: Access token successfully refreshed", "info");

        this._currentToken = AccessToken.NowRequested(result.access_token as string, result.expires_in as number, result.refresh_token as string);
        await this.onAccessTokenChangedAsyncEvent.emitAsync(this._currentToken.tokenValue);
        return true;
    }

    private tokenWillBeExpiredIn(token: AccessToken, seconds: number): boolean {
        return DateTimeService.now().add(seconds, "seconds").isAfter(token.expiresOn);
    }

    private async authenticateAsync(returnUrl: string): Promise<void> {
        this.logger.log("Logging in", "info");

        const { oAuthServer, client } = await this.initializeOAuthAsync();

        const redirect_uri = `${self.location.protocol}//${self.location.host}/loginCallback`;

        if (oAuthServer.code_challenge_methods_supported?.includes('S256') !== true) {
            this.logger.log("OAuth2: No S256 PKCE support, cannot authenticate!", "fatal");
            throw new Error();
        }

        const code_verifier = oauth.generateRandomCodeVerifier();
        localStorage.setItem(AuthenticationService.AuthCodeVerifierName, code_verifier);
        const code_challenge = await this.getSha256Async(code_verifier);
        const code_challenge_method = 'S256';

        const authorizationUrl = new URL(oAuthServer.authorization_endpoint!);
        authorizationUrl.searchParams.set('client_id', client.client_id);
        authorizationUrl.searchParams.set('code_challenge', code_challenge);
        authorizationUrl.searchParams.set('code_challenge_method', code_challenge_method);
        authorizationUrl.searchParams.set('redirect_uri', redirect_uri);
        authorizationUrl.searchParams.set('response_type', 'code');
        authorizationUrl.searchParams.set('scope', 'openid');
        authorizationUrl.searchParams.set('state', Base64Converter.fromString(returnUrl));
        await this.waitForStorageItemAsync(localStorage, AuthenticationService.AuthCodeVerifierName, code_verifier);
        window.location.href = authorizationUrl.toString();
    }

    private async getSha256Async(input: string): Promise<string> {
        if (window.crypto?.subtle) {
            return await oauth.calculatePKCECodeChallenge(input);
        }

        if (config.developerMode) {
            return Base64.stringify(sha256(input));
        }

        this.logger.log("OAuth2: Cannot calculate SHA256 hash.", "fatal");
        throw new Error("Cannot calculate SHA256 hash.");
    }

    public async processAuthenticationCallbackAsync(): Promise<{ returnUrl: string, hasCorrigibleError: boolean }> {

        const { oAuthServer, client } = await this.initializeOAuthAsync();

        const code_verifier = localStorage.getItem(AuthenticationService.AuthCodeVerifierName);
        localStorage.removeItem(AuthenticationService.AuthCodeVerifierName);
        const redirect_uri = `${self.location.protocol}//${self.location.host}/loginCallback`;

        if (isNullOrEmptyString(code_verifier)) {
            this.logger.log("OAuth2: cannot find the code verifier in the storage, will redirect to the main page to re-initiate the flow", "warn");
            
            return {
                returnUrl: "/",
                hasCorrigibleError: true
            };
        }

        const currentUrl: URL = new URL(window.location.href);
        const params = oauth.validateAuthResponse(oAuthServer, client, currentUrl, oauth.skipStateCheck);
        if (oauth.isOAuth2Error(params)) {
            this.logger.log(`OAuth2.validateAuthResponse error: ${params.error}`, "fatal");
            throw new Error();
        }

        const response = await oauth.authorizationCodeGrantRequest(
            oAuthServer,
            client,
            params,
            redirect_uri,
            code_verifier,
        );

        let challenges: oauth.WWWAuthenticateChallenge[] | undefined;
        if ((challenges = oauth.parseWwwAuthenticateChallenges(response))) {
            this.logger.log(`OAuth2.parseWwwAuthenticateChallenges unexpectedly responded with challenges: ${challenges.join(",")}`, "fatal");
            throw new Error();
        }

        const result = await oauth.processAuthorizationCodeOpenIDResponse(oAuthServer, client, response);
        if (oauth.isOAuth2Error(result)) {
            this.logger.log(`OAuth2.processAuthorizationCodeOpenIDResponse error, will redirect to the main page to re-initiate the flow: ${result.error} (${result.error_description})`, "error");

            return {
                returnUrl: "/",
                hasCorrigibleError: true
            };
        }

        this._currentToken = AccessToken.NowRequested(result.access_token, result.expires_in, result.refresh_token);
        await this.onLoggedInAsyncEvent.emitAsync();
        await this.onAccessTokenChangedAsyncEvent.emitAsync(this._currentToken.tokenValue);

        return {
            returnUrl: Base64Converter.toString(params.get("state")),
            hasCorrigibleError: false
        };
    }

    public changePasswordAsync(): Promise<void> {
        if (!isNullOrEmptyString(config.oAuth.passwordChangeUrl)) {
            window.location.href = config.oAuth.passwordChangeUrl;
        }
        return Promise.resolve();
    }

    private oAuthServerPromise: Promise<oauth.AuthorizationServer> | null = null;

    private async initializeOAuthAsync(): Promise<{ oAuthServer: oauth.AuthorizationServer, client: oauth.Client }> {

        this.oAuthServerPromise ??= (async () => {
            const issuer = new URL(config.oAuth.issuerUrl);
            return await oauth
                .discoveryRequest(issuer)
                .then((response) => oauth.processDiscoveryResponse(issuer, response));
        })();

        return {
            oAuthServer: await this.oAuthServerPromise,
            client: {
                client_id: config.oAuth.clientId,
                token_endpoint_auth_method: 'none',
            }
        };
    }

    // NOTE: When setting an item in the session storage, it is not guaranteed to be persisted immediately,
    // thus, it is possible that a navigation (which causes the teardown of the current page)
    // may interrupt the persisting operation resulting in the item being lost. To avoid that,
    // this method can be used to poll the sesssion storage for a short while, hopefully giving the browser
    // enough time to perform the persistence of the item.
    private waitForStorageItemAsync(
        storage: Storage,
        itemName: string,
        expectedValue: string,
        timeoutMs: number = 2000,
        pollingIntervalMs: number = 50
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const handle = setInterval(() => {
                const currentValue = storage.getItem(itemName);
                if (isNullOrUndefined(currentValue) || currentValue !== expectedValue) {
                    if (Date.now() - startTime > timeoutMs) {
                        reject(new Error(`Failed to set storage item '${itemName}' in ${timeoutMs} ms.`));
                    }

                    return;
                }

                clearInterval(handle);
                resolve();
            }, pollingIntervalMs);
        });
    }
}
